<template>
  <div v-cloak>
    <transition name="fade" appear appear-active-class="fade-enter-active">
      <div v-if="apiLoaded" class="wrap block">
        <div v-if="!ratingSections.length" class="ratings-placeholder">
          Расчет рейтингов субъектов РФ/ЦЗН будет осуществляться с 2027 года.<br>
          Первый расчет рейтингов будет выполнен по итогам 2026 года.
        </div>

        <section v-for="section in ratingSections" v-else :key="section.key" class="ratings-section">
          <h3>{{ section.title }}</h3>

          <div class="list">
            <div class="report-list">
              <ActivityMonitorRating
                  v-for="item in section.indicators"
                  :key="section.key + '_rating_' + (item.indicator.id || item.reportId)"
                  :title="item.title"
                  :indicator="item.indicator"
                  :period="period"
                  :level="section.level"
                  :legend="item.isComposite ? null : legend"
                  :report-data-function="item.dataFunction"
                  :report-id="item.reportId"
                  :region-and-department-info="regionAndDepartmentInfo"
                  :only-current-region="section.onlyCurrentRegion"
                  :only-current-department="section.onlyCurrentDepartment"
              />
            </div>
          </div>
        </section>
      </div>
      <div v-else style="height:1000px"></div>
    </transition>
  </div>
</template>

<script>
import ActivityMonitorRating from "./ActivityMonitorRating.vue";
import {createRatingDataFunction} from "@/modules/ActivityMonitor";
import {
  FEDERAL_VIEW_ROLES,
  REGIONAL_ROLES,
  REGIONAL_VIEW_ROLES,
  TERRITORIAL_ROLES,
} from "@/modules/ActivityMonitorRoles";
import {isRatingEnabled, isUserMonitorRegion, isMonitorRegion, hasRegionRating, hasCznRating, getCompositeRatingIndicatorId} from "@/modules/ActivityMonitorRatingRegions";

const LEGEND = 'Доля фактического значения от планового, %';

export default {
  name: "ActivityMonitorRatings",
  components: {ActivityMonitorRating},
  props: ['regionAndDepartmentInfo', 'period', 'configIndicators'],
  data: () => ({
    apiLoaded: false,
    legend: LEGEND,
  }),
  computed: {
    role() {
      return this.$user.role;
    },
    selectedRegionCode() {
      return this.regionAndDepartmentInfo?.region?.code;
    },
    ratingSections() {
      if (!isRatingEnabled()) return [];

      const limitedAccess = REGIONAL_ROLES.includes(this.role) || TERRITORIAL_ROLES.includes(this.role);
      const userInList = isUserMonitorRegion(this.$user);

      if (limitedAccess && !userInList) return [];

      const sections = [];
      const compositeId = getCompositeRatingIndicatorId();

      if (FEDERAL_VIEW_ROLES.includes(this.role)) {
        const indicators = [];
        if (compositeId) {
          indicators.push({
            indicator: {id: compositeId},
            title: 'Сводный рейтинг',
            dataFunction: createRatingDataFunction(compositeId),
            reportId: 8,
            isComposite: true,
          });
        }
        indicators.push(...this.mapRatingIndicators(
            (this.configIndicators || []).filter(hasRegionRating)
        ));
        if (indicators.length) {
          sections.push({
            key: 'federal',
            title: 'Федеральные рейтинги',
            indicators,
            level: 'region',
            onlyCurrentRegion: false,
            onlyCurrentDepartment: false,
          });
        }
      }

      if (REGIONAL_VIEW_ROLES.includes(this.role) && isMonitorRegion(this.selectedRegionCode)) {
        const indicators = [];
        if (compositeId) {
          indicators.push({
            indicator: {id: compositeId},
            title: 'Сводный рейтинг',
            dataFunction: createRatingDataFunction(compositeId),
            reportId: 9,
            isComposite: true,
          });
        }
        indicators.push(...this.mapRatingIndicators(
            (this.configIndicators || []).filter(i => !i.isOnlyRegional && hasCznRating(i))
        ));
        if (indicators.length) {
          sections.push({
            key: 'regional',
            title: `Региональные рейтинги (${this.regionAndDepartmentInfo.region.name})`,
            indicators,
            level: 'czn',
            onlyCurrentRegion: false,
            onlyCurrentDepartment: false,
          });
        }
      }
      return sections;
    },
  },
  watch: {
    regionAndDepartmentInfo: {
      handler() {
        this.reload();
      },
      deep: true,
    },
    period: {
      handler() {
        this.reload();
      },
    },
  },
  methods: {
    mapRatingIndicators(indicators) {
      if (!indicators) return [];

      return indicators
          .filter(i => i?.rating?.id)
          .map(indicator => ({
            indicator,
            title: `${indicator.rating.code}. ${indicator.name}`,
            dataFunction: createRatingDataFunction(indicator.rating.id),
          }));
    },
    reload() {
      this.apiLoaded = false;
      this.$nextTick(() => {
        this.apiLoaded = true;
      });
    },
  },
  async beforeMount() {
    this.apiLoaded = true;
  },
};
</script>

<style scoped lang="scss">
@import "../../styles/main.scss";

.ratings-section + .ratings-section {
  margin-top: 32px;
}

.report-list {
  border-top: 1px solid #e8e8e9;

  .report-list__item {
    display: flex;
    flex-flow: row wrap;
    gap: 20px;
    justify-content: space-between;
    border-bottom: 1px solid #e8e8e9;
    padding: 5px 0;

    .report-list__title {
      flex: 0 0 calc(100% - 40px);
      display: flex;
      flex-flow: row nowrap;
      align-items: center;

      .toggleSwitch {
        margin-right: 5px;
      }

      span {
        color: $cblue;
      }
    }

    .report-list__download {
      background: url(~@/assets/download.svg) no-repeat center center;
      background-size: contain;
      flex: 0 0 20px;
      width: 20px;
      height: 20px;
      cursor: pointer;
    }
  }
}

.ratings-placeholder {
  color: #777779;
  line-height: 1.6;
}

.list {
  table {
    margin-bottom: 25px;

    th {
      border-bottom: 1px solid #E8E8E9;
    }

    td:nth-child(2),
    th:nth-child(2) {
      text-align: right;
    }

    tr:last-child td {
      border-bottom: 1px solid #E8E8E9;
    }
  }
}
</style>
